import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Invoices.css'; // reuse same styling
import { FaCalendarCheck, FaSearch, FaSort, FaSortUp, FaSortDown, FaSync, FaUndo, FaPlus } from 'react-icons/fa';
import { showToast, hideToast, updateToast } from '../utils/toast';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_LIMIT = 50;
const SORT_KEY = 'weeklyInvoiceSort';

// simple local storage helpers
function safeLocalGet(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; }
}
function safeLocalSet(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}
function setUserPref(key, val) {
  safeLocalSet(key, val);
}
function getUserPref(key) {
  return safeLocalGet(key);
}
function getUserFullName() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}') || {};
    const nm = [u.firstname, u.lastname].filter(Boolean).join(' ');
    return nm || u.name || u.username || '';
  } catch { return ''; }
}

function formatDateYYYYMMDD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fridaysOfMonth(year, monthIndex0) {
  // monthIndex0: 0-11
  const fridays = [];
  // Start from the 1st of the month
  const d = new Date(year, monthIndex0, 1);
  // Find the first Friday (5 = Friday in JS where Sun=0)
  const day = d.getDay();
  const offset = (5 - day + 7) % 7; // days until first Friday
  d.setDate(d.getDate() + offset);
  // Collect all Fridays in the month (4 or 5 depending on month)
  while (d.getMonth() === monthIndex0) {
    fridays.push(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setDate(d.getDate() + 7);
  }
  return fridays;
}

export default function WeeklyInvoicing() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ offset: 0, limit: DEFAULT_LIMIT, total: 0 });

  // filters (similar to Invoices)
  const [q, setQ] = useState('');

  // modal + warehouses
  const [wiOpen, setWiOpen] = useState(false);
  const [wiClient, setWiClient] = useState(null);
  const [wiSelectedIds, setWiSelectedIds] = useState([]);
  const [lastResults, setLastResults] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [wiSubmitting, setWiSubmitting] = useState(false);
  const [wiForm, setWiForm] = useState({
    warehouseId: 'all',            // 'all' or a specific id
    perWarehouse: false,           // separate invoice per warehouse (kept)
    month: '',                     // YYYY-MM
    friday: '',                    // YYYY-MM-DD (selected Friday within month)
    invoiceDate: new Date().toISOString().slice(0,10), // YYYY-MM-DD
    allowMinimumWhenNoReceipts: false,
  });

  // derived Fridays for selected month
  const fridaysForMonth = useMemo(() => {
    const ym = String(wiForm.month || '');
    const mMatch = ym.match(/^(\d{4})-(\d{1,2})$/);
    if (!mMatch) return [];
    const y = Number(mMatch[1]);
    const m0 = Number(mMatch[2]) - 1;
    return fridaysOfMonth(y, m0);
  }, [wiForm.month]);

  // Ensure friday stays in sync with month selection
  useEffect(() => {
    if (!wiForm.month) return;
    if (!wiForm.friday) {
      if (fridaysForMonth.length > 0) setWiForm(f => ({ ...f, friday: formatDateYYYYMMDD(fridaysForMonth[0]) }));
    } else {
      // If chosen Friday no longer in month (user changed month), reset to first
      const stillValid = fridaysForMonth.some(d => formatDateYYYYMMDD(d) === wiForm.friday);
      if (!stillValid && fridaysForMonth.length > 0) setWiForm(f => ({ ...f, friday: formatDateYYYYMMDD(fridaysForMonth[0]) }));
    }
  }, [wiForm.month, wiForm.friday, fridaysForMonth]);

  // persisted sort
  const initialSort = useMemo(() => {
    try {
      const saved = safeLocalGet(SORT_KEY) || getUserPref(SORT_KEY);
      return saved || { field: 'name', dir: 'ASC' };
    } catch { return { field: 'name', dir: 'ASC' }; }
  }, []);
  const [sort, setSort] = useState(initialSort);
  const [selected, setSelected] = useState(new Set());
  const selAllRef = useRef(null);
  useEffect(() => {
    if (selAllRef.current) {
      selAllRef.current.indeterminate = selected.size > 0 && selected.size < rows.length;
    }
  }, [selected, rows.length]);

  useEffect(() => {
    safeLocalSet(SORT_KEY, sort);
    setUserPref(SORT_KEY, sort);
  }, [sort]);

  // load warehouses once for dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/warehouses`);
        const j = await res.json();
        setWarehouses(Array.isArray(j) ? j : []);
      } catch (e) {
        console.error('warehouses load failed', e);
        setWarehouses([]);
      }
    })();
  }, []);

  useEffect(() => {
    const subtitle = '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Weekly Invoicing ', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [meta?.total]);

  useEffect(() => { fetchClients(0, meta.limit); }, [q, sort]);

  async function fetchClients(offset = 0, limit = DEFAULT_LIMIT) {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('offset', String(offset));
      qs.set('limit', String(limit));
      qs.set('activeOnly', '1');
      if (q) qs.set('q', q);
      // Only list clients with Weekly storage billing frequency
      qs.set('weeklyOnly', '1');
      // include sorting per clients.js API
      const sf = (sort && sort.field) ? sort.field : 'name';
      const so = (sort && sort.dir) ? sort.dir : 'ASC';
      qs.set('sortField', sf);
      qs.set('sortOrder', so);

      const res = await fetch(`${API}/api/clients?${qs.toString()}`);
      const j = await res.json();

      const list = Array.isArray(j?.data) ? j.data
        : (Array.isArray(j?.rows) ? j.rows
        : (Array.isArray(j) ? j : []));
      const total = (j?.meta && typeof j.meta.total === 'number') ? j.meta.total
        : (typeof j?.total === 'number' ? j.total : list.length);

      setRows(list);
      setSelected(new Set());
      setMeta({ offset, limit, total });
    } catch (e) {
      console.error('fetch clients failed', e);
      setRows([]);
      setSelected(new Set());
      setMeta({ offset, limit, total: 0 });
    } finally { setLoading(false); }
  }

  function handleRun() { fetchClients(0, meta.limit); }
  function handleClear() { setQ(''); fetchClients(0, meta.limit); }

  function toggleSort(field) {
    setSort((prev) => {
      const dir = prev.field === field ? (prev.dir === 'ASC' ? 'DESC' : 'ASC') : 'ASC';
      return { field, dir };
    });
  }
  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = (checked) => {
    if (checked) {
      setSelected(new Set(rows.map(r => r.id)));
    } else {
      setSelected(new Set());
    }
  };

  const sorted = useMemo(() => {
    const data = [...rows];
    const { field, dir } = sort || { field: 'name', dir: 'ASC' };
    data.sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];
      const isNum = ['id','paymentterms','status'].includes(field);
      if (isNum) {
        const an = Number(aVal ?? 0);
        const bn = Number(bVal ?? 0);
        return dir === 'ASC' ? an - bn : bn - an;
      } else {
        const av = String(aVal ?? '').toLowerCase();
        const bv = String(bVal ?? '').toLowerCase();
        if (av < bv) return dir === 'ASC' ? -1 : 1;
        if (av > bv) return dir === 'ASC' ? 1 : -1;
        return 0;
      }
    });
    return data;
  }, [rows, sort]);

  function iconFor(field) {
    if (!sort || sort.field !== field) return <FaSort />;
    return sort.dir === 'ASC' ? <FaSortUp /> : <FaSortDown />;
  }

  function openWeeklyInvoice(client) {
    setWiClient(client);
    setWiSelectedIds(client?.id ? [client.id] : []);
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const defaultMonth = `${y}-${m}`;
    const fr = fridaysOfMonth(y, now.getMonth());
    setWiForm(f => ({
      ...f,
      month: defaultMonth,
      friday: fr.length ? formatDateYYYYMMDD(fr[0]) : '',
      warehouseId: 'all',
      perWarehouse: false,
      allowMinimumWhenNoReceipts: false,
    }));
    setWiOpen(true);
  }

  function openWeeklyForSelected(){
    const ids = Array.from(selected || []);
    if (!ids.length) { alert('Select at least one client.'); return; }
    const first = ids.length === 1 ? rows.find(r => r.id === ids[0]) : null;
    setWiClient(first || null);
    setWiSelectedIds(ids);
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const defaultMonth = `${y}-${m}`;
    const fr = fridaysOfMonth(y, now.getMonth());
    setWiForm(f => ({
      ...f,
      month: defaultMonth,
      friday: fr.length ? formatDateYYYYMMDD(fr[0]) : '',
      warehouseId: 'all',
      perWarehouse: false,
      allowMinimumWhenNoReceipts: false,
    }));
    setWiOpen(true);
  }

  async function submitWeeklyInvoice(e){
    e?.preventDefault?.();
    if (!wiForm.month) { alert('Please select which month this invoice is for.'); return; }
    if (!wiForm.friday) { alert('Please select which Friday this invoice is for.'); return; }
    const ids = wiSelectedIds.length ? wiSelectedIds : (wiClient?.id ? [wiClient.id] : []);
    if (!ids.length) { alert('No client selected'); return; }

    try {
      setWiSubmitting(true);
      showToast({ id: 'weekly.create', message: 'Creating weekly invoices…', duration: 0, align: 'center', slideFrom: 'top', pill: true });
      const payload = {
        clientIds: ids,
        warehouseId: wiForm.warehouseId === 'all' ? 'all' : Number(wiForm.warehouseId),
        invoiceDate: wiForm.invoiceDate,
        whichMonth: wiForm.month,
        whichFriday: wiForm.friday,
        createdBy: { name: getUserFullName() || 'system' },
        perWarehouse: !!wiForm.perWarehouse,
        allowMinimumWhenNoReceipts: !!wiForm.allowMinimumWhenNoReceipts,
      };
      const res = await fetch(`${API}/api/invoices/weekly/storage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || 'Create failed');
      const ok = Array.isArray(j?.results) ? j.results.filter(r=>r.ok) : [];
      const fail = Array.isArray(j?.results) ? j.results.filter(r=>!r.ok) : [];
      setLastResults({ ok, fail });
      updateToast({ id: 'weekly.create', message: `Weekly invoices created: ${ok.length}/${ids.length}${fail.length ? ` (failed ${fail.length})` : ''}` });
      setTimeout(() => hideToast('weekly.create'), 2500);
      try { window.dispatchEvent(new CustomEvent('invoices:refresh')); } catch {}
      setWiOpen(false);
    } catch (err) {
      console.error('weekly create failed', err);
      updateToast({ id: 'weekly.create', message: err.message || 'Failed to create weekly invoices' });
      setTimeout(() => hideToast('weekly.create'), 3000);
    }
    finally { setWiSubmitting(false); }
  }

  return (
    <div className="invoices-wrapper">
      <div className="invoice-filters">
        <div className="preset-controls">
          <label>Search</label>
          <div style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
            <FaSearch />
            <input
              type="text"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Search clients…"
            />
          </div>
        </div>
        <button className="btn" onClick={handleRun} title="Run"><FaSync style={{ marginRight: 6 }} /> Run</button>
        <button className="btn" onClick={handleClear} title="Reset"><FaUndo style={{ marginRight: 6 }} /> Reset</button>
        <button
          className="btn primary"
          disabled={selected.size === 0}
          onClick={openWeeklyForSelected}
          title={selected.size ? `Create for ${selected.size} selected client(s)` : 'Select clients first'}
        >
          <FaPlus style={{ marginRight: 6 }} /> Create for Selected ({selected.size || 0})
        </button>
      </div>

      <div className="invoice-table-wrapper">
        <table className="invoice-table">
          <thead>
            <tr>
              <th style={{ width: 34, textAlign:'center' }}>
                <input
                  type="checkbox"
                  ref={selAllRef}
                  checked={rows.length > 0 && selected.size === rows.length}
                  onChange={(e)=>toggleAll(e.target.checked)}
                  style={{ width:16, height:16 }}
                />
              </th>
              <th className="actions-cell">Actions</th>
              <th onClick={()=>toggleSort('id')} className={sort.field==='id' ? 'active-sort' : ''}>
                ID {iconFor('id')}
              </th>
              <th onClick={()=>toggleSort('name')} className={sort.field==='name' ? 'active-sort' : ''}>
                Name {iconFor('name')}
              </th>
              <th onClick={()=>toggleSort('contact')} className={sort.field==='contact' ? 'active-sort' : ''}>
                Contact {iconFor('contact')}
              </th>
              <th onClick={()=>toggleSort('email')} className={sort.field==='email' ? 'active-sort' : ''}>
                Email {iconFor('email')}
              </th>
              <th onClick={()=>toggleSort('paymentterms')} className={sort.field==='paymentterms' ? 'active-sort' : ''}>
                Terms {iconFor('paymentterms')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <tr key={c.id}>
                <td style={{ textAlign:'center' }}>
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={()=>toggleRow(c.id)}
                    style={{ width:16, height:16 }}
                  />
                </td>
                <td className="actions-cell">
                  <button className="mini-btn" title="Create weekly invoice" onClick={()=>openWeeklyInvoice(c)}>
                    <FaCalendarCheck size={16} />
                  </button>
                </td>
                <td className="nowrap">{c.id}</td>
                <td className="nowrap">{c.name}</td>
                <td className="nowrap">{c.contact}</td>
                <td className="nowrap">{c.email}</td>
                <td className="nowrap">{c.paymentterms}</td>
              </tr>
            ))}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={7} style={{ opacity:.7, textAlign:'center', padding:'1rem' }}>No clients found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {lastResults && (lastResults.ok?.length > 0 || lastResults.fail?.length > 0) && (
        <div className="toast" style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid #444', borderRadius: 8 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Weekly invoice results</div>
          {lastResults.ok?.length > 0 && (
            <div style={{ marginBottom: 4 }}>
              Created:&nbsp;
              {lastResults.ok.map(r => r.invoiceId).slice(0,10).map((id) => (
                <a key={id} href={`#/invoice/${id}`} style={{ marginRight: 8, textDecoration: 'underline' }}>#{id}</a>
              ))}
              {lastResults.ok.length > 10 ? `… (+${lastResults.ok.length - 10} more)` : ''}
            </div>
          )}
          {lastResults.fail?.length > 0 && (
            <div style={{ opacity: .8 }}>
              Failed client(s): {lastResults.fail.map(r => r.clientId).join(', ')}
            </div>
          )}
        </div>
      )}

      {wiOpen && (
        <div className="modal-overlay weekly-modal-overlay" onMouseDown={(e)=>{ if (e.target===e.currentTarget) setWiOpen(false); }}>
          <div className="modal weekly-modal" role="dialog" aria-modal="true" aria-labelledby="wiTitle">
            <h2 id="wiTitle" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <FaCalendarCheck /> Create Weekly Invoice{wiSelectedIds?.length > 1 ? ` — ${wiSelectedIds.length} clients` : (wiClient?.name ? ` — ${wiClient.name}` : '')}
            </h2>
            <form onSubmit={submitWeeklyInvoice} className="payment-form">
              <div className="form-grid" style={{ gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap: '0.6rem 0.8rem' }}>
                <label>Warehouse(s) selection
                  <select
                    value={wiForm.warehouseId}
                    onChange={(e)=>setWiForm(f=>({ ...f, warehouseId: e.target.value }))}
                  >
                    <option value="all">All</option>
                    {warehouses.map(w => (
                      <option key={w.id} value={String(w.id)}>{w.name || `Warehouse #${w.id}`}</option>
                    ))}
                  </select>
                </label>

                <label>Which month is this invoice for?
                  {(() => {
                    const now = new Date();
                    const curYear = now.getFullYear();
                    const ym = String(wiForm.month || '').split('-');
                    const selYear = ym[0] && ym[0].length === 4 ? ym[0] : String(curYear);
                    const selMonth = ym[1] ? ym[1].padStart(2, '0') : String(now.getMonth() + 1).padStart(2, '0');
                    const MONTHS = [
                      ['01','January'], ['02','February'], ['03','March'], ['04','April'],
                      ['05','May'], ['06','June'], ['07','July'], ['08','August'],
                      ['09','September'], ['10','October'], ['11','November'], ['12','December'],
                    ];
                    const YEARS = Array.from({ length: 7 }, (_, i) => String(curYear - 3 + i));
                    const setMonth = (mm) => setWiForm(f => ({ ...f, month: `${selYear}-${mm}` }));
                    const setYear  = (yy) => setWiForm(f => ({ ...f, month: `${yy}-${selMonth}` }));
                    return (
                      <div style={{ display:'flex', gap: 8 }}>
                        <select value={selMonth} onChange={(e) => setMonth(e.target.value)}>
                          {MONTHS.map(([val, name]) => (
                            <option key={val} value={val}>{name}</option>
                          ))}
                        </select>
                        <select value={selYear} onChange={(e) => setYear(e.target.value)}>
                          {YEARS.map(y => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </label>

                <label>Which Friday?
                  <select
                    value={wiForm.friday}
                    onChange={(e)=>setWiForm(f => ({ ...f, friday: e.target.value }))}
                  >
                    {fridaysForMonth.map((d, idx) => {
                      const label = d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
                      const val = formatDateYYYYMMDD(d);
                      return <option key={val} value={val}>{label}</option>;
                    })}
                  </select>
                </label>

                <div style={{ display:'flex', gap:'2rem', marginTop:'0.5rem', gridColumn:'1 / -1' }}>
                  <label style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <input
                      type="checkbox"
                      style={{ width:'16px', height:'16px' }}
                      checked={wiForm.perWarehouse}
                      onChange={(e)=>setWiForm(f=>({ ...f, perWarehouse: e.target.checked }))}
                    />
                    <span>Separate Invoice For Each Warehouse Location</span>
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <input
                      type="checkbox"
                      style={{ width:'16px', height:'16px' }}
                      checked={wiForm.allowMinimumWhenNoReceipts || false}
                      onChange={(e)=>setWiForm(f=>({ ...f, allowMinimumWhenNoReceipts: e.target.checked }))}
                    />
                    <span>Allow minimum-only if no receipts</span>
                  </label>
                </div>
              </div>

              <div className="modal-actions" style={{ justifyContent:'flex-end', marginTop: 12 }}>
                <button type="button" className="btn" onClick={()=>setWiOpen(false)} disabled={wiSubmitting}>Cancel</button>
                <button type="submit" className="btn primary" disabled={wiSubmitting}>{wiSubmitting ? 'Creating…' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
